import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { HttpErrorResponse } from '@angular/common/http';

import { AppService} from 'app/services/app.service';
import { fuseAnimations } from '@fuse/animations';
import { FuseConfigService } from '@fuse/services/config.service';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { FuseSplashScreenService } from '@fuse/services/splash-screen.service';
import { AuthService, CheckUserStatues } from 'app/services/auth.service';

@Component({
    selector: 'app-reset-password',
    templateUrl: './reset-password.component.html',
    styleUrls: ['./reset-password.component.scss'],
    animations: fuseAnimations,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ResetPasswordComponent implements OnInit, OnDestroy{

	resetForm: FormGroup;
	resetFormErrors: any;
	token: string;
	email: string;
	saving = false;
	invalidLink = false;
	lastErrorMessage: string;

	private _unsubscribeAll: Subject<void>;

	constructor(
		private router: Router,
		private appService: AppService,
		private authService: AuthService,
		private formBuilder: FormBuilder,
		private translateService: TranslateService,
		private _fuseConfigService: FuseConfigService,
		private _fuseSplashService: FuseSplashScreenService,
		private activatedRoute: ActivatedRoute
	){
		this.resetFormErrors = {
			password: {},
			password_confirmation: {}
		};
		this._fuseConfigService.config = {
			layout: {
				navbar   : {
					hidden: true
				},
				toolbar  : {
					hidden: true
				},
				footer   : {
					hidden: true
				},
				sidepanel: {
					hidden: true
				}
			}
		};

		this._unsubscribeAll = new Subject();
	}

	ngOnInit(): void{
		this.resetForm = this.formBuilder.group({
			password: ['', Validators.required],
			password_confirmation: ['', Validators.required],
		}, {
			validators: ResetPasswordComponent.passwordsMatch
		});

		this.resetForm.valueChanges
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(() => {
			this.onResetFormValuesChanged();
		});

		this.authService.chekcUserStatus
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(status => {
			if(status === CheckUserStatues.CHECKED){
				this._fuseSplashService.hide();
			}
		});

		this.token = this.activatedRoute.snapshot.queryParamMap.get('token');
		this.email = this.activatedRoute.snapshot.queryParamMap.get('email');

		// senza token/email il POST fallirebbe comunque: meglio dirlo subito
		if(!this.token || !this.email){
			this.invalidLink = true;
			this.resetForm.disable();
		}
	}

	ngOnDestroy(): void{
		this._unsubscribeAll.next();
		this._unsubscribeAll.complete();
	}

	/**
	 * Le due password devono coincidere: il backend lo verifica con `confirmed`,
	 * ma senza questo l'utente lo scopre solo dopo l'invio.
	 */
	static passwordsMatch(group: AbstractControl): ValidationErrors | null{
		const password = group.get('password');
		const confirmation = group.get('password_confirmation');
		if(!password || !confirmation) return null;
		if(!confirmation.value) return null;
		return password.value === confirmation.value ? null : { passwordsMismatch: true };
	}

	onResetFormValuesChanged(): void{
		for(const field in this.resetFormErrors){
			if(!Object.prototype.hasOwnProperty.call(this.resetFormErrors, field)){
				continue;
			}

			// Clear previous errors
			this.resetFormErrors[field] = {};

			// Get the control
			const control = this.resetForm.get(field);

			if(control && control.dirty && !control.valid){
				this.resetFormErrors[field] = control.errors;
			}
		}
	}

	onSubmitClick(): void{
		if(this.resetForm.invalid || this.saving || this.invalidLink) return;

		this.lastErrorMessage = null;
		this.saving = true;
		this.resetForm.disable();

		this.authService.resetPassword({
			email: this.email,
			token: this.token,
			password: this.resetForm.value.password,
			password_confirmation: this.resetForm.value.password_confirmation
		})
		.pipe(takeUntil(this._unsubscribeAll))
		.subscribe(response => {
			this.saving = false;

			if(response && response.success === false){
				this.resetForm.enable();
				this.lastErrorMessage = response.message || this.translateService.instant('RESET_PASSWORD.ERROR.GENERIC');
				return;
			}

			this.appService.showSuccessMessage(this.translateService.instant('RESET_PASSWORD.SUCCESS'));
			this.router.navigate(['auth/login']);
		}, response => {
			// handleError mostra gia' la snackbar, qui si ripristina il form
			this.saving = false;
			this.resetForm.enable();
			this.lastErrorMessage = this.translateService.instant('RESET_PASSWORD.ERROR.GENERIC');
			if(response instanceof HttpErrorResponse && response.error){
				// token scaduto o gia' usato: il backend risponde 400 con errors.email
				const message = response.error.message
					|| (response.error.errors && response.error.errors.email);
				if(message) this.lastErrorMessage = message;
			}
		});
	}
}
